import React, { useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { ShieldCheck, ScrollText, Send, Loader2, BarChart3, CheckCircle2, AlertTriangle, ArrowRight } from "lucide-react";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Link } from "react-router-dom";
import SessionsList from "../components/SessionsList";
import { useAuth, API } from "../context/AuthContext";
import { useFmtXof } from "../lib/i18nFmt";

const MONTHS_FR = ["janvier","février","mars","avril","mai","juin","juillet","août","septembre","octobre","novembre","décembre"];
const MONTHS_EN = ["January","February","March","April","May","June","July","August","September","October","November","December"];

function prevMonth(y, m) { if (m === 1) return [y - 1, 12]; return [y, m - 1]; }

export default function AdminDashboard() {
  const { t, i18n } = useTranslation();
  const fmt = useFmtXof();
  const isEn = (i18n.resolvedLanguage || i18n.language || "fr").startsWith("en");
  const MONTHS = isEn ? MONTHS_EN : MONTHS_FR;

  const { user, loading } = useAuth();
  const today = new Date();
  const [defaultY, defaultM] = prevMonth(today.getFullYear(), today.getMonth() + 1);
  const [year, setYear] = useState(defaultY);
  const [month, setMonth] = useState(defaultM);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const monthOptions = useMemo(() => {
    const opts = [];
    let [y, m] = [today.getFullYear(), today.getMonth() + 1];
    for (let i = 0; i < 12; i++) {
      opts.push({ y, m, label: `${MONTHS[m - 1]} ${y}` });
      [y, m] = prevMonth(y, m);
    }
    return opts;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [MONTHS]);

  const runBatch = async (dryRun) => {
    setSending(true); setError(""); setResult(null);
    try {
      const { data } = await axios.post(`${API}/admin/reports/monthly/send`, { year, month, dry_run: dryRun });
      setResult(data);
    } catch (e) {
      setError(e?.response?.data?.detail || t("adminDash.sendingFailed"));
    } finally { setSending(false); }
  };

  if (loading || user === null) {
    return <div className="min-h-[60vh] flex items-center justify-center text-[#16A34A]">{t("common.loading")}</div>;
  }
  if (!user || user.role !== "admin") return <Navigate to="/dashboard" replace />;

  return (
    <div className="min-h-screen bg-[#FAFCFB]" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="rounded-3xl bg-white border border-[#E5EBE7] p-8 sm:p-10 relative overflow-hidden">
          <span className="geo-shape bg-[#16A34A]/12 h-64 w-64 -top-20 -right-20" aria-hidden />
          <div className="relative">
            <span className="pill"><ShieldCheck className="h-3.5 w-3.5" /> {t("adminDash.pill")}</span>
            <h1 className="mt-4 font-display text-4xl sm:text-5xl font-bold text-[#0A2518]">{t("adminDash.title")}</h1>
            <p className="mt-3 text-[#4B5563] max-w-2xl">{t("adminDash.intro")}</p>
            <div className="mt-5">
              <Button asChild className="rounded-full bg-[#16A34A] hover:bg-[#15803D] text-white" data-testid="admin-go-kpis">
                <Link to="/admin/kpis"><BarChart3 className="h-4 w-4 mr-2" /> {t("adminKpi.title")} <ArrowRight className="h-4 w-4 ml-2" /></Link>
              </Button>
            </div>
          </div>
        </div>

        <Card className="mt-8 p-6 border-[#E5EBE7]" data-testid="admin-monthly-reports-card">
          <h2 className="font-display text-2xl font-semibold text-[#0A2518] mb-1 flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-[#16A34A]" /> {t("adminDash.reportsTitle")}
          </h2>
          <p className="text-sm text-[#4B5563] mb-5">{t("adminDash.reportsHelp")}</p>

          <div className="flex items-center gap-3 flex-wrap">
            <select
              data-testid="admin-monthly-period-select"
              value={`${year}-${month}`}
              onChange={(e) => { const [y, m] = e.target.value.split("-").map(Number); setYear(y); setMonth(m); }}
              className="rounded-full border border-[#E5EBE7] bg-white px-4 py-2 text-sm text-[#0A2518] focus:outline-none focus:ring-2 focus:ring-[#16A34A]/30 capitalize"
            >
              {monthOptions.map((o) => (<option key={`${o.y}-${o.m}`} value={`${o.y}-${o.m}`} className="capitalize">{o.label}</option>))}
            </select>
            <Button data-testid="admin-monthly-dryrun-btn" variant="outline" disabled={sending} onClick={() => runBatch(true)} className="rounded-full border-[#16A34A] text-[#16A34A] hover:bg-[#F0FDF4]">
              {sending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}{t("adminDash.dryrun")}
            </Button>
            <Button data-testid="admin-monthly-send-btn" disabled={sending} onClick={() => runBatch(false)} className="rounded-full bg-[#16A34A] hover:bg-[#15803D] text-white">
              {sending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}{t("adminDash.sendBtn")}
            </Button>
          </div>

          {error && (
            <div className="mt-4 rounded-xl bg-red-50 border border-red-200 text-red-700 p-4 text-sm flex items-start gap-2" data-testid="admin-monthly-error">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" /> {error}
            </div>
          )}

          {result && (
            <div className="mt-5 rounded-xl bg-[#F0FDF4] border border-[#16A34A]/30 p-4 text-sm text-[#0A2518]" data-testid="admin-monthly-result">
              <div className="flex items-center gap-2 mb-2 font-semibold">
                <CheckCircle2 className="h-4 w-4 text-[#16A34A]" />
                {result.dry_run ? t("adminDash.resultPreview") : t("adminDash.resultDone")} — <span className="capitalize">{result.period_label}</span>
              </div>
              <div className="grid sm:grid-cols-4 gap-3 mt-2">
                <Stat label={t("adminDash.statCandidates")} value={result.candidates} />
                <Stat label={t("adminDash.statSent")} value={result.sent} />
                <Stat label={t("adminDash.statSkipped")} value={result.skipped} />
                <Stat label={t("adminDash.statFailed")} value={result.failed} tone={result.failed ? "warn" : "ok"} />
              </div>
              {Array.isArray(result.details) && result.details.length > 0 && (
                <div className="mt-4 max-h-48 overflow-auto rounded-lg bg-white border border-[#E5EBE7]">
                  <table className="w-full text-xs">
                    <thead className="text-[#6B7280] sticky top-0 bg-white">
                      <tr><th className="text-left p-2">{t("adminDash.thParent")}</th><th className="text-right p-2">{t("adminDash.thHours")}</th><th className="text-right p-2">{t("adminDash.thAmount")}</th><th className="text-right p-2">{t("adminDash.thStatus")}</th></tr>
                    </thead>
                    <tbody>
                      {result.details.map((d, i) => (
                        <tr key={i} className="border-t border-[#E5EBE7]">
                          <td className="p-2 text-[#0A2518]">{d.parent_email}</td>
                          <td className="p-2 text-right">{(d.totals?.hours || 0).toFixed(1)} h</td>
                          <td className="p-2 text-right">{fmt(d.totals?.amount_xof || 0)}</td>
                          <td className="p-2 text-right">
                            {result.dry_run ? <span className="text-[#6B7280]">{t("adminDash.statusPreview")}</span> : (d.sent ? <span className="text-[#15803D]">{t("adminDash.statusSent")}</span> : <span className="text-red-600">{t("adminDash.statusFail")}</span>)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </Card>

        <Card className="mt-8 p-6 border-[#E5EBE7]">
          <h2 className="font-display text-2xl font-semibold text-[#0A2518] mb-1 flex items-center gap-2">
            <ScrollText className="h-5 w-5 text-[#16A34A]" /> {t("adminDash.sessionsTitle")}
          </h2>
          <p className="text-sm text-[#4B5563] mb-5">{t("adminDash.sessionsHelp")}</p>
          <SessionsList mode="admin" />
        </Card>
      </div>
    </div>
  );
}

function Stat({ label, value, tone = "ok" }) {
  const color = tone === "warn" && Number(value) > 0 ? "text-red-600" : "text-[#0A2518]";
  return (
    <div className="rounded-lg bg-white border border-[#E5EBE7] p-3">
      <div className="text-[11px] uppercase tracking-wider text-[#6B7280]">{label}</div>
      <div className={`text-xl font-display font-bold ${color}`}>{value ?? 0}</div>
    </div>
  );
}
